// Step 18 — AI Deals list + per-deal home.
//
// Reads `context.workspaces` (the same list the broker `/projects`
// page implicitly uses via for_user). No new endpoints.
//
// AIDealsList  — /deals — table of every workspace the AI user can
//                access (auth is enforced server-side; we never invent
//                or reveal a workspace the API didn't already return).
//
// AIDealHome   — /deals/:workspaceId — landing surface for one deal.
//                Confirms the workspace exists in context; if not,
//                renders an empty state (not a 404 page — the user
//                may have lost access, may have mistyped, etc.).

import { Link } from "react-router-dom";

import {
  AIEmpty,
  AILoading,
  AIPageHeader,
  AISection,
} from "../shell/AIPrimitives";
import { useAIWorkspace } from "../shell/AIWorkspaceContext";

export function AIDealsList() {
  const { loading, workspaces } = useAIWorkspace();
  if (loading) return <AILoading label="Laddar affärer…" />;

  return (
    <>
      <AIPageHeader
        title="Affärer"
        subtitle={`${workspaces.length} tillgängliga`}
      />
      {workspaces.length === 0 ? (
        <AIEmpty
          title="Inga affärer kopplade till ditt konto"
          hint="AI-medlemskap behöver läggas till av en admin."
        />
      ) : (
        <table className="qai-deals-table">
          <thead>
            <tr>
              <th>Organisation</th>
              <th>Steg</th>
              <th>Status</th>
              <th>Ägare</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {workspaces.map((w) => (
              <tr key={w.id}>
                <td>
                  <Link to={`/deals/${w.id}`} className="qai-deals-link">
                    {w.organisation_name}
                  </Link>
                </td>
                <td>{w.deal_stage || "—"}</td>
                <td>
                  <span
                    className="qai-shell-header-status"
                    data-status={w.deal_status || ""}
                  >
                    {w.deal_status || "—"}
                  </span>
                </td>
                <td>{w.owner_company_name || "—"}</td>
                <td>
                  <Link to={`/deals/${w.id}`}>Öppna →</Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

export function AIDealHome() {
  const { loading, activeWorkspace, activeWorkspaceId } = useAIWorkspace();
  if (loading) return <AILoading />;

  if (!activeWorkspace) {
    return (
      <>
        <AIPageHeader title="Affär" />
        <AIEmpty
          title="Affären finns inte i din kontext"
          hint="Kontrollera att du fortfarande är AI-medlem i denna affär."
        />
      </>
    );
  }

  const links = [
    { to: `/deals/${activeWorkspaceId}/buyers`, title: "Köpare", hint: "Portfolio + intelligence per köpare" },
    { to: `/deals/${activeWorkspaceId}/intelligence`, title: "Intelligence", hint: "Vad Qada vet om bolaget och affären" },
    { to: `/deals/${activeWorkspaceId}/risks`, title: "Risker", hint: "Registrerade risker från riskprofilen" },
    { to: `/deals/${activeWorkspaceId}/contracts`, title: "Avtal", hint: "Utkast, godkännande, signering" },
    { to: `/deals/${activeWorkspaceId}/actions`, title: "Åtgärder", hint: "Föreslagna och pågående åtgärder" },
    { to: `/deals/${activeWorkspaceId}/activity`, title: "Aktivitet", hint: "Snapshot-historik och åtgärdshistorik" },
  ];

  return (
    <>
      <AIPageHeader
        title={activeWorkspace.organisation_name}
        subtitle={
          activeWorkspace.deal_stage
            ? `Steg: ${activeWorkspace.deal_stage}`
            : undefined
        }
      />
      <AISection>
        <div className="qai-deal-tiles">
          {links.map((l) => (
            <Link key={l.to} to={l.to} className="qai-deal-tile">
              <div className="qai-deal-tile-title">{l.title}</div>
              <div className="qai-deal-tile-hint">{l.hint}</div>
            </Link>
          ))}
        </div>
      </AISection>
    </>
  );
}
